import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, User2, ShieldCheck, Save } from 'lucide-react';
import { LearnerDetailView } from '@/components/admin/LearnerDetailView';

const ROLE_LABELS = {
    super_admin: 'Super Admin', company_admin: 'Company Admin', trainer: 'Trainer', trainee: 'Trainee',
};

export default function AdminLearnerDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');

    useEffect(() => {
        setData(null); setErr('');
        api.get(`/admin/users/${userId}/detail`)
            .then((r) => setData(r.data))
            .catch(() => setErr('Failed to load learner detail'));
    }, [userId]);

    const u = data?.user;
    return (
        <div data-testid="admin-learner-detail-page">
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4" data-testid="learner-back-btn">
                <ArrowLeft className="h-4 w-4" /> Back to reports
            </button>

            <div className="flex items-center gap-3 mb-5">
                <span className="grid place-items-center w-12 h-12 rounded-2xl bg-brand-blue/10 text-brand-blue"><User2 className="h-6 w-6" /></span>
                <div>
                    <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900" data-testid="learner-name">{u?.full_name || (err ? 'Learner' : 'Loading…')}</h1>
                    {u && <p className="text-sm text-slate-500">{u.email} · {ROLE_LABELS[u.role] || u.role}{u.employee_id ? ` · ${u.employee_id}` : ''}{u.region ? ` · ${u.region}` : ''}</p>}
                </div>
            </div>

            {err && <div className="p-6 text-sm text-rose-600 bg-white rounded-2xl border border-slate-200">{err}</div>}
            {!data && !err && <div className="p-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}
            {data && (
                <>
                    <ParticipantFlags userId={userId} initial={data.participant} />
                    <LearnerDetailView data={data} />
                </>
            )}
        </div>
    );
}

const CLUSTERS = ['UP & UK', 'Rajasthan', 'Punjab / JK / HP', 'Haryana / Delhi', 'Bihar / JH', 'West Bengal',
    'Odisha & NE', 'Maharashtra / Goa', 'Gujarat', 'MP / CH', 'Tamil Nadu', 'Karnataka', 'AP / TG', 'Kerala'];

function ParticipantFlags({ userId, initial }) {
    const [f, setF] = useState(initial || {});
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/admin/participants/${userId}/flags`, {
                kyc_status: f.kyc_status || '',
                fraud_flag: !!f.fraud_flag,
                duplicate_flag: !!f.duplicate_flag,
                disqualified: !!f.disqualified,
                state_branch: f.state_branch || '',
                weekly_reward_won: f.weekly_reward_won || '',
                wild_card: !!f.wild_card,
            });
            toast.success('Contest flags saved');
        } catch { toast.error('Failed to save flags'); } finally { setBusy(false); }
    };

    return (
        <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-4 mb-5" data-testid="participant-flags-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-slate-800 font-bold"><ShieldCheck className="h-4 w-4 text-brand-blue" /> Contest eligibility flags</div>
                {(f.pincode || f.city || f.state) && <div className="text-xs text-slate-500">{[f.city, f.state, f.pincode].filter(Boolean).join(' · ')}</div>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <label className="block">
                    <span className="text-xs font-semibold text-slate-600">KYC status</span>
                    <select value={f.kyc_status || ''} onChange={(e) => set('kyc_status', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="flag-kyc">
                        <option value="">Pending</option>
                        <option value="completed">Completed</option>
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-600">State / Branch cluster</span>
                    <select value={f.state_branch || ''} onChange={(e) => set('state_branch', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="flag-state-branch">
                        <option value="">Unmapped</option>
                        {CLUSTERS.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Weekly reward won</span>
                    <select value={f.weekly_reward_won || ''} onChange={(e) => set('weekly_reward_won', e.target.value)} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white" data-testid="flag-reward-won">
                        <option value="">None</option>
                        <option value="tshirt">T-Shirt</option>
                        <option value="cash">Cash</option>
                        <option value="smartphone">Smartphone</option>
                    </select>
                </label>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3">
                {[['fraud_flag', 'Fraud flagged'], ['duplicate_flag', 'Duplicate'], ['disqualified', 'Disqualified'], ['wild_card', 'Wild card entry']].map(([k, l]) => (
                    <label key={k} className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={!!f[k]} onChange={(e) => set(k, e.target.checked)} className="h-4 w-4 rounded" data-testid={`flag-${k}`} /> {l}
                    </label>
                ))}
                <div className="flex-1" />
                <button onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-black text-brand-yellow text-sm font-bold disabled:opacity-60" data-testid="flag-save-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save flags
                </button>
            </div>
        </div>
    );
}
